import { jwtDecode } from 'jwt-decode';
import { clearAccess, loadAccess } from '../../auth/access';
import { createContext, useCallback, useContext, useEffect, useReducer, ReactNode } from 'react';
import { renewTokenIfDue, resetAuthLogoutGuard, setAuthLogoutHandler } from '../../api/apiClient';
import { normalizeRole, Session } from '../../auth/session';
import { config } from '../../config';
import { logError } from '../../utils/logger';
import { ResetPasswordPayload } from './auth.api';
import * as authService from './auth.service';

/**
 * Writes the session to storage and returns it with the role normalized.
 *
 * The API reports roles capitalized (`Analyst`, `HR`); the UI compares against
 * lower-case keys. Returning the normalized session matters: dispatching the raw
 * one left `role` as `"Analyst"` in React state until the next reload, so every
 * role comparison failed — the sidebar rendered empty and `HomeByRole` fell
 * through to the 404 page.
 */
const persistSession = ({ token, user, role }: Session): Session => {
  const roleNormalized = normalizeRole(role);

  localStorage.setItem(config.AUTH_LS_KEYS.TOKEN, token);
  localStorage.setItem(config.AUTH_LS_KEYS.USER, JSON.stringify(user));
  localStorage.setItem(config.AUTH_LS_KEYS.ROLE, roleNormalized);

  return { token, user, role: roleNormalized };
};

const clearSession = (): void => {
  localStorage.removeItem(config.AUTH_LS_KEYS.TOKEN);
  localStorage.removeItem(config.AUTH_LS_KEYS.USER);
  localStorage.removeItem(config.AUTH_LS_KEYS.ROLE);
};

const getStoredSession = (): Session | null => {
  const token = localStorage.getItem(config.AUTH_LS_KEYS.TOKEN);
  if (!token) return null;

  const userRaw = localStorage.getItem(config.AUTH_LS_KEYS.USER);
  const role = localStorage.getItem(config.AUTH_LS_KEYS.ROLE);

  return {
    token,
    user: userRaw ? JSON.parse(userRaw) : null,
    role,
  };
};

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: AuthState = {
  session: getStoredSession(),
  loading: false,
  error: null,
  initialized: false,
};

const TYPES = {
  REQUEST: 'REQUEST',
  SUCCESS: 'SUCCESS',
  // Request finished successfully but produced no session — a password change, not a login.
  DONE: 'DONE',
  ERROR: 'ERROR',
  LOGOUT: 'LOGOUT',
  INIT: 'INIT',
} as const;

type AuthAction =
  | { type: 'REQUEST' }
  | { type: 'SUCCESS'; payload: Session }
  | { type: 'DONE' }
  | { type: 'ERROR'; error: string }
  | { type: 'LOGOUT' }
  | { type: 'INIT' };

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case TYPES.REQUEST:
      return { ...state, loading: true, error: null };
    case TYPES.SUCCESS:
      return { ...state, session: action.payload, loading: false, error: null };
    case TYPES.DONE:
      return { ...state, loading: false, error: null };
    case TYPES.ERROR:
      return { ...state, loading: false, error: action.error };
    case TYPES.LOGOUT:
      return { ...initialState, initialized: true };
    case TYPES.INIT:
      return { ...state, initialized: true };
    default:
      return state;
  }
}

interface AuthContextValue {
  session: Session | null;
  user: Session['user'] | undefined;
  role: string | null | undefined;
  isAuthenticated: boolean;
  isReady: boolean;
  loading: boolean;
  error: string | null;
  login: (...args: any[]) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (payload: ResetPasswordPayload) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const createAuthAction = useCallback(
    (fn: (...args: any[]) => Promise<Session>) =>
      async (...args: any[]): Promise<{ success: boolean; error?: string }> => {
        dispatch({ type: TYPES.REQUEST });
        try {
          const session = persistSession(await fn(...args));
          resetAuthLogoutGuard();
          // A new login, maybe another role: its menu replaces whatever was cached.
          clearAccess();
          await loadAccess(true).catch(() => undefined);
          dispatch({ type: TYPES.SUCCESS, payload: session });
          return { success: true };
        } catch (err) {
          const message = logError({
            message: 'Authentication failed',
            error: err,
            code: 'AUTH_ACTION_FAILED',
          });
          dispatch({ type: TYPES.ERROR, error: message });
          return { success: false, error: message };
        }
      },
    []
  );

  const login = createAuthAction(authService.login);

  /**
   * Deliberately NOT built with `createAuthAction`: that helper feeds the response to
   * `persistSession`, and `POST /api/auth/reset-password` answers with a bare
   * `{ message }` — no token, no user. Running it through would write the string
   * "undefined" into the token and user keys and drop the role back to its default,
   * i.e. changing your password would corrupt the session you were changing it from.
   */
  const resetPassword = useCallback(
    async (payload: ResetPasswordPayload): Promise<{ success: boolean; error?: string }> => {
      dispatch({ type: TYPES.REQUEST });
      try {
        await authService.resetPassword(payload);
        dispatch({ type: TYPES.DONE });
        return { success: true };
      } catch (err) {
        const message = logError({
          message: 'Password reset failed',
          error: err,
          code: 'AUTH_ACTION_FAILED',
        });
        dispatch({ type: TYPES.ERROR, error: message });
        return { success: false, error: message };
      }
    },
    []
  );

  const logout = useCallback(async (): Promise<void> => {
    if (!localStorage.getItem(config.AUTH_LS_KEYS.TOKEN)) return;
    try {
      await authService.logout();
    } catch {
      /* server failure shouldn't block local logout */
    } finally {
      clearSession();
      clearAccess();
      dispatch({ type: TYPES.LOGOUT });
    }
  }, []);

  useEffect(() => {
    setAuthLogoutHandler(() => {
      logout();
    });
  }, [logout]);

  /**
   * Typing and clicking count as activity too, not only API calls: a user filling in a
   * long form can go a long while without a request, and should not be logged out for it.
   * Checked at most once a minute; `renewTokenIfDue` does nothing until renewal is due.
   */
  const isSignedIn = !!state.session?.token;
  useEffect(() => {
    if (!isSignedIn) return;
    let lastCheck = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastCheck < 60_000) return;
      lastCheck = now;
      renewTokenIfDue();
    };
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('pointerdown', onActivity, { passive: true });
    return () => {
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('pointerdown', onActivity);
    };
  }, [isSignedIn]);

  useEffect(() => {
    (async () => {
      const stored = getStoredSession();
      if (!stored?.token) {
        return dispatch({ type: TYPES.INIT });
      }

      try {
        const { exp } = jwtDecode<{ exp: number }>(stored.token);
        if (Date.now() < exp * 1000) {
          dispatch({ type: TYPES.SUCCESS, payload: stored });
        } else {
          const freshSession = persistSession(await authService.getSession());
          dispatch({ type: TYPES.SUCCESS, payload: freshSession });
        }
      } catch {
        clearSession();
        dispatch({ type: TYPES.LOGOUT });
      } finally {
        dispatch({ type: TYPES.INIT });
      }
    })();
  }, []);

  const value: AuthContextValue = {
    session: state.session,
    user: state.session?.user,
    role: state.session?.role,
    isAuthenticated: !!state.session?.token,
    isReady: state.initialized,
    loading: state.loading,
    error: state.error,
    login,
    resetPassword,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
